import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Brand } from "@/components/Logo";
import { useLang } from "@/i18n";
import { meta } from "@/analytics/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResult() {
  const { result } = useParams();
  const [params] = useSearchParams();
  const { t } = useLang();
  const [status, setStatus] = useState(result === "success" ? "pending" : "cancelled");
  const purchaseSent = useRef(false);

  useEffect(() => {
    if (result !== "success") return;
    const provider = params.get("provider");
    const ppToken = params.get("token");
    const sid = provider === "paypal" ? (ppToken ? `pp_${ppToken}` : null) : params.get("session_id");
    if (!sid) return;
    let tries = 0;
    let poll = null;
    const start = async () => {
      if (provider === "paypal" && ppToken) {
        try {
          await fetch(`${API}/paypal/capture`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ paypal_order_id: ppToken }),
          });
        } catch {}
      }
      poll = setInterval(async () => {
        tries += 1;
        try {
          const r = await fetch(`${API}/payments/status/${sid}`);
          const d = await r.json();
          if (d.payment_status === "paid") {
            setStatus("paid");
            clearInterval(poll);
            if (!purchaseSent.current && d.amount) {
              purchaseSent.current = true;
              meta.purchase({
                value: d.amount,
                currency: (d.currency || "eur").toUpperCase(),
                content_ids: d.lookup_key ? [d.lookup_key] : [],
                content_type: "product",
                num_items: 1,
              });
            }
          }
        } catch {}
        if (tries > 10) clearInterval(poll);
      }, 2000);
    };
    start();
    return () => { if (poll) clearInterval(poll); };
  }, [result, params]);

  const ok = status === "paid";
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-5 text-center hero-glow" data-testid="payment-result-page">
      <Brand />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="mt-12 max-w-md">
        {result === "cancel" || status === "cancelled" ? (
          <>
            <XCircle size={56} className="mx-auto text-[#94A3B8]" />
            <h1 className="text-3xl font-extrabold text-[#003087] mt-6" data-testid="payment-cancel-title">{t("pay.cancel.title")}</h1>
            <p className="text-[#64748B] mt-3 text-sm">{t("pay.cancel.sub")}</p>
          </>
        ) : ok ? (
          <>
            <CheckCircle2 size={56} className="mx-auto text-[#00AEEF]" />
            <h1 className="text-3xl font-extrabold text-[#003087] mt-6" data-testid="payment-success-title">{t("pay.success.title")}</h1>
            <p className="text-[#64748B] mt-3 text-sm leading-relaxed">{t("pay.success.sub")}</p>
          </>
        ) : (
          <>
            <Loader2 size={48} className="mx-auto text-[#00AEEF] animate-spin" />
            <p className="text-[#64748B] mt-6 text-sm" data-testid="payment-pending">{t("pay.pending")}</p>
          </>
        )}
        <Link to="/" data-testid="payment-home-link" className="btn-green inline-block mt-10 rounded-full px-8 py-3.5 text-sm font-bold">
          {t("pay.home")}
        </Link>
      </motion.div>
    </div>
  );
}
